import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Gift, Copy, Check, Share2, MessageCircle, Send, Mail, Linkedin, Facebook, Users, Sparkles } from "lucide-react";
import api from "../services/api";
import { track } from "../utils/track";
import { shareUrls } from "../utils/referral";

// Botones directos. Los iconos de marca no existen en lucide: colores de la marca + icono genérico. Los tonos son los de
// marca OSCURECIDOS hasta contraste ≥ 4,5:1 con el texto blanco (WCAG AA): el verde de WhatsApp #25D366 daba 1,98:1.
const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, cls: "bg-[#0E7A4E] hover:bg-[#0B6A43] text-white" },
  { key: "telegram", label: "Telegram", icon: Send, cls: "bg-[#1A74A3] hover:bg-[#155F87] text-white" },
  { key: "email", label: "Email", icon: Mail, cls: "bg-gray-800 hover:bg-gray-900 text-white" },
  { key: "linkedin", label: "LinkedIn", icon: Linkedin, cls: "bg-[#0A66C2] hover:bg-[#08529b] text-white" },
  { key: "x", label: "X", icon: Share2, cls: "bg-black hover:bg-gray-800 text-white" },
  { key: "facebook", label: "Facebook", icon: Facebook, cls: "bg-[#1565C0] hover:bg-[#0F4F9C] text-white" },
];

export default function ReferralsPage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    track("referral_view");
    let alive = true;
    api
      .getReferrals()
      .then((r) => alive && setData(r))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, []);

  const pages = data?.reward_pages ?? 20;
  const text = t("referrals.share_text", { pages });
  const subject = t("referrals.email_subject", { pages });
  const urls = useMemo(() => (data?.link ? shareUrls({ link: data.link, text, subject }) : {}), [data?.link, text, subject]);
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const copy = async () => {
    track("referral_copy");
    try {
      await navigator.clipboard.writeText(data.link);
    } catch {
      const el = document.createElement("textarea");
      el.value = data.link;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const nativeShare = async () => {
    track("referral_share_click", { channel: "native" });
    try {
      await navigator.share({ title: t("referrals.share_title", { pages }), text, url: data.link });
    } catch {
      /* el usuario cerró el menú de compartir */
    }
  };

  return (
    <motion.main initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-6 max-w-3xl mx-auto space-y-6 min-h-screen pb-20">
      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-primary-100 text-primary-600 rounded-xl">
            <Gift className="w-6 h-6" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{t("referrals.title")}</h1>
        </div>
        <p className="text-gray-600">{t("referrals.subtitle", { pages })}</p>
      </header>

      {error && <p className="rounded-xl bg-red-50 text-red-700 p-4 text-sm">{t("referrals.error")}</p>}
      {!data && !error && <p className="text-gray-500">{t("referrals.loading")}</p>}

      {data && (
        <>
          <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
            <label htmlFor="referral-link" className="text-sm font-semibold text-gray-700">{t("referrals.link")}</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                id="referral-link"
                readOnly
                value={data.link}
                onFocus={(e) => e.target.select()}
                data-testid="referral-link"
                className="flex-1 min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-800"
              />
              <button
                onClick={copy}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-semibold px-4 py-2.5"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? t("referrals.copied") : t("referrals.copy")}
              </button>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              {canNativeShare && (
                <button
                  onClick={nativeShare}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary-50 text-primary-700 border border-primary-200 font-semibold px-4 py-2.5"
                >
                  <Share2 className="w-4 h-4" /> {t("referrals.share")}
                </button>
              )}
              {CHANNELS.map(({ key, label, icon: Icon, cls }) => (
                <a
                  key={key}
                  href={urls[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("referral_share_click", { channel: key })}
                  className={`inline-flex items-center gap-2 rounded-xl font-semibold px-4 py-2.5 ${cls}`}
                >
                  <Icon className="w-4 h-4" /> {label}
                </a>
              ))}
            </div>
          </section>

          <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              [Users, data.invited, t("referrals.invited")],
              [Sparkles, data.rewarded, t("referrals.converted")],
              [Gift, data.pages_earned, t("referrals.pages")],
            ].map(([Icon, n, label]) => (
              <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                <Icon className="w-5 h-5 text-primary-600 mb-2" />
                <div className="text-3xl font-bold text-gray-900">{n}</div>
                <div className="text-sm text-gray-500">{label}</div>
              </div>
            ))}
          </section>
          {data.pending > 0 && <p className="text-sm text-gray-500">{data.pending} · {t("referrals.pending")}</p>}
          <p className="text-sm text-gray-500">{t("referrals.cap", { n: data.monthly_cap_left })}</p>

          <section className="bg-gray-50 rounded-2xl border border-gray-100 p-5">
            <h2 className="font-bold text-gray-900 mb-2">{t("referrals.r_title")}</h2>
            <ul className="list-disc ml-5 space-y-1.5 text-sm text-gray-700">
              <li>{t("referrals.r1")}</li>
              <li>{t("referrals.r2", { pages })}</li>
              <li>{t("referrals.r3", { cap: data.monthly_cap })}</li>
              <li>{t("referrals.r4")}</li>
            </ul>
          </section>
        </>
      )}
    </motion.main>
  );
}
